import { AnyAction, Middleware, MiddlewareAPI, ThunkDispatch } from '@reduxjs/toolkit';
import { RootState } from 'store';
import { APITimecardsSlice, updateTimecardByIdCaches } from 'store/api/timecards';
import {
    resetTimecardState,
    setStopwatchRunning,
    setTrackedTimecardId,
    switchTrackedTimecard,
    trackTimecard,
} from 'store/reducers/timecard';
import { isTourMode } from 'tour/tourMode';
import { UserData } from 'types/user';
import { getLocalStorage, removeLocalStorage, setLocalStorage } from 'utils/localStorage';
import {
    createTimecardBroadcastChannel,
    generateTabId,
    isBroadcastChannelSupported,
    isTimecardBroadcastMessage,
    TimecardBroadcastMessage,
} from 'utils/timecardBroadcast';

// Module-level variables to maintain channel state across middleware calls
let broadcastChannel: BroadcastChannel | null = null;
const tabId: string = generateTabId();
let currentUserId: string | null = null;
let currentTenantId: string | null = null;
let messageHandlerAttached = false;

/**
 * Broadcasts a message to other tabs
 */
const broadcastMessage = (message: TimecardBroadcastMessage) => {
    if (!broadcastChannel) {
        // If channel doesn't exist but API is supported, try to reinitialize
        if (isBroadcastChannelSupported() && currentUserId && currentTenantId) {
            initializeBroadcastChannel(currentUserId, currentTenantId);
        }
    }

    if (broadcastChannel) {
        try {
            broadcastChannel.postMessage(message);
        } catch (error) {
            console.error('Failed to broadcast message:', error);
        }
    }
};

/**
 * Exported function to broadcast timecard events (for use in API mutations)
 */
export const broadcastTimecardEvent = broadcastMessage;

/**
 * Get the current tab ID
 */
export const getTabId = () => tabId;

/**
 * Initializes the BroadcastChannel for a given user and tenant
 */
const initializeBroadcastChannel = (userId: string, tenantId: string) => {
    // Close existing channel if it exists
    if (broadcastChannel) {
        broadcastChannel.close();
        broadcastChannel = null;
        messageHandlerAttached = false;
    }

    currentUserId = userId;
    currentTenantId = tenantId;

    if (isBroadcastChannelSupported()) {
        try {
            broadcastChannel = createTimecardBroadcastChannel(userId, tenantId);
        } catch (error) {
            console.error('Failed to create BroadcastChannel:', error);
        }
    }
};

/**
 * Sets up the message handler for the broadcast channel
 */
const setupMessageHandler = (store: MiddlewareAPI<ThunkDispatch<RootState, unknown, AnyAction>, RootState>) => {
    if (messageHandlerAttached || !broadcastChannel) return;

    broadcastChannel.addEventListener('message', (event: MessageEvent) => {
        const message = event.data;

        // Ignore messages from this tab
        if (!isTimecardBroadcastMessage(message)) {
            return;
        }

        if (message.payload.senderId === tabId) {
            return;
        }

        const state = store.getState() as RootState;

        switch (message.type) {
            case 'TRACKING_STARTED':
            case 'TRACKING_RESUMED': {
                const { timecardId, totalTimeInSeconds, stopwatchRunningSince } = message.payload;
                // Only update if different from current state
                if (state.timecard.trackedTimecardId !== timecardId || !state.timecard.isStopwatchRunning) {
                    if (currentUserId && currentTenantId) {
                        // Update Redux state
                        store.dispatch(
                            trackTimecard({
                                timecardId: parseInt(timecardId),
                                userId: parseInt(currentUserId),
                                tenantId: currentTenantId,
                                isStopwatchRunning: true,
                            })
                        );

                        // Update the cache immediately with the time values from the server (passed via broadcast)
                        updateTimecardByIdCaches(store.dispatch, parseInt(timecardId), {
                            isStopwatchRunning: true,
                            totalTimeInSeconds,
                            stopwatchRunningSince,
                        });

                        // Also invalidate tags to ensure full consistency. The bare 'Timecards' list
                        // tag refreshes list queries in this tab too — e.g. the matter header's own-draft
                        // check — so its start-tracking button hides when another tab starts a draft.
                        store.dispatch(
                            APITimecardsSlice.util.invalidateTags([
                                'TrackedTimecard',
                                'Timecards',
                                { type: 'Timecards', id: parseInt(timecardId) },
                            ])
                        );
                    }
                }
                break;
            }

            case 'TRACKING_PAUSED': {
                const { timecardId, totalTimeInSeconds } = message.payload;
                // Only update if this is the currently tracked timecard and it's running
                if (state.timecard.trackedTimecardId === timecardId && state.timecard.isStopwatchRunning) {
                    // Update Redux state FIRST so timer stops immediately
                    store.dispatch(setStopwatchRunning(false));

                    // Update the cache immediately with the time value from the server (passed via broadcast)
                    updateTimecardByIdCaches(store.dispatch, parseInt(timecardId), {
                        isStopwatchRunning: false,
                        totalTimeInSeconds,
                        stopwatchRunningSince: null,
                    });

                    // Also invalidate tags to ensure full consistency
                    store.dispatch(
                        APITimecardsSlice.util.invalidateTags([
                            'TrackedTimecard',
                            { type: 'Timecards', id: parseInt(timecardId) },
                        ])
                    );
                }
                break;
            }

            case 'TRACKING_FINISHED': {
                const { timecardId } = message.payload;
                // Only reset if this is the currently tracked timecard
                if (state.timecard.trackedTimecardId === timecardId) {
                    if (currentUserId && currentTenantId) {
                        // Reset the Redux state
                        store.dispatch(
                            resetTimecardState({
                                userId: parseInt(currentUserId),
                                tenantId: currentTenantId,
                            })
                        );

                        // Invalidate RTK Query cache to force refetch (more reliable than manual updates)
                        store.dispatch(
                            APITimecardsSlice.util.invalidateTags([
                                'TrackedTimecard',
                                'Timecards',
                                { type: 'Timecards', id: parseInt(timecardId) },
                            ])
                        );
                    }
                }
                break;
            }

            case 'STATE_SYNC_REQUEST': {
                const { senderId: requesterId } = message.payload;
                // Respond with current state
                const responseMessage: TimecardBroadcastMessage = {
                    type: 'STATE_SYNC_RESPONSE',
                    payload: {
                        timecardId: state.timecard.trackedTimecardId || null,
                        isRunning: state.timecard.isStopwatchRunning,
                        senderId: tabId,
                        requesterId,
                    },
                };
                broadcastMessage(responseMessage);
                break;
            }

            case 'STATE_SYNC_RESPONSE': {
                const { timecardId, isRunning, requesterId } = message.payload;
                // Only process if this response is for us
                if (requesterId === tabId && timecardId && currentUserId && currentTenantId) {
                    // Update state if we don't have a tracked timecard but another tab does
                    if (!state.timecard.trackedTimecardId && timecardId) {
                        store.dispatch(
                            trackTimecard({
                                timecardId: parseInt(timecardId),
                                userId: parseInt(currentUserId),
                                tenantId: currentTenantId,
                                isStopwatchRunning: isRunning,
                            })
                        );
                    }
                }
                break;
            }
        }
    });

    messageHandlerAttached = true;
};

export const timecardPersistenceMiddleware: Middleware<object, RootState> = (store) => {
    return (next) => (action) => {
        // Capture state BEFORE action for resetTimecardState
        const stateBeforeAction = store.getState();
        const trackedTimecardIdBeforeReset = resetTimecardState.match(action)
            ? stateBeforeAction.timecard.trackedTimecardId
            : undefined;

        const result = next(action);
        const state = store.getState();
        // The timer is paused through the guided tour: its tracked timecard stays stored, and other tabs hear nothing.
        if (isTourMode()) {
            return result;
        }

        // Handle localStorage sync and channel initialization after the action has been processed
        // Note: Broadcasting is now done in API mutations after server response
        if (trackTimecard.match(action)) {
            const { timecardId, userId, tenantId } = action.payload;
            if (userId && tenantId) {
                setLocalStorage(`${userId}-${tenantId}-tracked-timecard-id`, String(timecardId));

                // Initialize channel if needed
                if (!broadcastChannel || currentUserId !== String(userId) || currentTenantId !== tenantId) {
                    initializeBroadcastChannel(String(userId), tenantId);
                    setupMessageHandler(store);
                }
            }
        }

        if (resetTimecardState.match(action)) {
            const { userId, tenantId } = action.payload;

            if (userId && tenantId) {
                removeLocalStorage(`${userId}-${tenantId}-tracked-timecard-id`);

                // Broadcast that tracking finished (use the ID we captured before the reset)
                if (trackedTimecardIdBeforeReset) {
                    broadcastMessage({
                        type: 'TRACKING_FINISHED',
                        payload: {
                            timecardId: trackedTimecardIdBeforeReset,
                            senderId: tabId,
                        },
                    });
                }
            }
        }

        if (switchTrackedTimecard.match(action)) {
            const userData: UserData = action.payload;
            const userId = userData.id;
            const tenantId = userData.tenants[0];

            if (userId && tenantId) {
                const storedTimecardId = getLocalStorage(`${userId}-${tenantId}-tracked-timecard-id`);

                // Close old channel and open new one for the new user/tenant
                initializeBroadcastChannel(String(userId), tenantId);
                setupMessageHandler(store);

                // Update the state with the stored timecard ID
                if (storedTimecardId && state.timecard.trackedTimecardId !== storedTimecardId) {
                    store.dispatch(setTrackedTimecardId(storedTimecardId));
                }

                // Request state sync from other tabs
                broadcastMessage({
                    type: 'STATE_SYNC_REQUEST',
                    payload: {
                        senderId: tabId,
                    },
                });
            }
        }

        return result;
    };
};
